import { logD, logW } from "@blacknut/logging/dist";
import { GameStreamMetric } from "@blacknut/playerwebrtc";
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { PlayerStatus } from "./PlayerUtils";
import { getLayoutMap, requestKeyboardLock } from "./keyboard";
import { LOGGING_TAG } from "./logging";
import { FullscreenHelper } from "@blacknut/javascript-sdk/dist";

const NOP = () => {
  console.warn("NOP");
};

export declare type RTCIceCandidatePair = {
  local: RTCIceCandidate;
  remote: RTCIceCandidate;
};

declare type TimestampedMetrics = GameStreamMetric & { timestamp: number };

export declare type PlayerContextT = {
  playerStatus: PlayerStatus;
  setPlayerStatus: (status: PlayerStatus) => void;
  fullscreen: boolean;
  candidatePair?: RTCIceCandidatePair;
  candidates: RTCIceCandidate[];
  setCandidatePair: (pair: RTCIceCandidatePair | undefined) => void;
  setCandidates: (candidates: RTCIceCandidate[]) => void;
  getKeyboardLayoutMap: () => Promise<"azerty" | "qwerty" | "qwertz" | undefined>;
  metrics: TimestampedMetrics[];
  pushMetrics: (metrics: GameStreamMetric) => void;
  fullscreenHelper?: FullscreenHelper;
  requestKeyboardLock?: () => Promise<void>;
  preferedWindowMode?: "windowed" | "fullscreen";
};
export const PlayerContext = createContext<PlayerContextT>({
  playerStatus: PlayerStatus.INIT,
  setPlayerStatus: NOP,
  fullscreen: !!document.fullscreenElement,
  setCandidatePair: NOP,
  candidates: [],
  setCandidates: NOP,
  metrics: [],
  pushMetrics: NOP,
  getKeyboardLayoutMap: () => Promise.reject(),
});

const MAX_TIMESTAMP = 5 * 60; /* 5 Mins*/
interface PlayerContextProps {
  getKeyboardLayoutMap?: () => Promise<"azerty" | "qwerty" | "qwertz" | undefined>;
  requestKeyboardLock?: () => Promise<void>;
  fullscreenHelper: FullscreenHelper;
  preferedWindowMode?: "windowed" | "fullscreen";
}
export const PlayerContextProvider = ({
  children,
  getKeyboardLayoutMap: inGetKeyboardLayoutMap,
  requestKeyboardLock: inRequestKeyboardLock,
  preferedWindowMode,
  fullscreenHelper,
}: PropsWithChildren<PlayerContextProps>) => {
  const [playerStatus, _setPlayerStatus] = useState(PlayerStatus.INIT);

  const setPlayerStatus = useCallback(
    (status: PlayerStatus) => {
      _setPlayerStatus((oldStatus) => {
        logD(LOGGING_TAG, "Status changed: %o=>%o", oldStatus, status);
        return status;
      });
    },
    [_setPlayerStatus],
  );

  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => {
    fullscreenHelper
      .isFullscreen()
      .then((e) => {
        setFullscreen(e);
      })
      .catch((e) => {
        logW(LOGGING_TAG, "Error getting fullscreen: %o", e);
      });
  }, [fullscreenHelper]);

  useEffect(() => {
    return () => {
      fullscreenHelper.teardown();
    };
  }, [fullscreenHelper]);

  const [candidatePair, setCandidatePair] = useState<
    { local: RTCIceCandidate; remote: RTCIceCandidate } | undefined
  >();
  const [candidates, setCandidates] = useState<RTCIceCandidate[]>([]);
  const [metrics, setMetrics] = useState<TimestampedMetrics[]>([]);
  const pushMetrics = useCallback((metric: GameStreamMetric) => {
    const timestamp = new Date().getTime() / 1000;
    setMetrics((v) => {
      const newMetrics = [...v];
      while (
        newMetrics.length > 0 &&
        timestamp - newMetrics[0].timestamp > MAX_TIMESTAMP
      ) {
        const e = newMetrics.splice(0, 1);
      }
      newMetrics.push({ ...metric, timestamp });
      return newMetrics;
    });
  }, []);

  useEffect(() => {
    const listener = (fullscreen: boolean) => {
      logD(LOGGING_TAG, "fullscreenchange: %o", fullscreen);
      setFullscreen(fullscreen);
    };
    fullscreenHelper.addFullcreenChangeListener(listener);
    return () => {
      fullscreenHelper.removeFullscreenChangeListener(listener);
    };
  }, [fullscreenHelper]);

  return (
    <PlayerContext.Provider
      value={{
        playerStatus,
        setPlayerStatus,
        fullscreen,
        candidatePair,
        setCandidatePair,
        candidates,
        setCandidates,
        metrics,
        pushMetrics,
        getKeyboardLayoutMap: inGetKeyboardLayoutMap || getLayoutMap,
        requestKeyboardLock: inRequestKeyboardLock || requestKeyboardLock,
        fullscreenHelper,
        preferedWindowMode,
      }}
    >
      {children}
    </PlayerContext.Provider>
  );
};

export const usePlayerContext = () => useContext(PlayerContext);
